import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { categoryBySlug, productsByCategory, type SubCategory } from "@/lib/catalog";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/shop/ProductCard";
import { ProductFilters } from "@/components/shop/ProductFilters";
import { WebshopGate } from "@/components/shop/WebshopGate";

export const Route = createFileRoute("/c/$category/")({
  codeSplitGroupings: [["loader", "component", "notFoundComponent", "errorComponent"]],
  loader: ({ params }) => {
    const cat = categoryBySlug(params.category);
    if (!cat) throw notFound();
    return { cat };
  },
  head: ({ loaderData, params }) =>
    loaderData
      ? {
          meta: [
            { title: `${loaderData.cat.name} — Webshop Elvion Group Nederland` },
            { name: "description", content: loaderData.cat.description },
            { name: "robots", content: "noindex" },
            { property: "og:title", content: `${loaderData.cat.name} — Webshop Elvion Group Nederland` },
            { property: "og:description", content: loaderData.cat.description },
            { property: "og:type", content: "website" },
            { property: "og:url", content: `https://elvion-group.nl/c/${params.category}` },
          ],
          links: [{ rel: "canonical", href: `https://elvion-group.nl/c/${params.category}` }],
        }
      : {},
  component: () => (
    <WebshopGate>
      <CategoryPage />
    </WebshopGate>
  ),
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Categorie niet gevonden</h1>
      <Link to="/c" className="mt-4 inline-block text-orange hover:underline">
        Terug naar webshop
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Er ging iets mis</h1>
      <p className="mt-2 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
    </main>
  ),
});

function CategoryPage() {
  const { cat } = Route.useLoaderData();
  const products = productsByCategory(cat.slug);

  return (
    <>
      <Header variant="shop" />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{cat.name}</h1>
        <p className="mt-2 max-w-3xl text-sm text-text-secondary sm:text-base">
          {cat.description}
        </p>

        {cat.subcategories.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {cat.subcategories.map((s: SubCategory) => (
              <Link
                key={s.slug}
                to="/c/$category/$subcategory"
                params={{ category: cat.slug, subcategory: s.slug }}
                className="rounded-full border border-border bg-card px-4 py-1.5 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
              >
                {s.name}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-8">
          {products.length === 0 ? (
            <p className="text-text-secondary">Nog geen producten in deze categorie.</p>
          ) : (
            <ProductFilters products={products} storageKey={`shop-filters:${cat.slug}`}>
              {(filtered) => (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((p) => (
                    <ProductCard key={p.slug} product={p} />
                  ))}
                  {filtered.length === 0 && (
                    <p className="col-span-full text-text-secondary">
                      Geen producten komen overeen met deze filters.
                    </p>
                  )}
                </div>
              )}
            </ProductFilters>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
